/**
 * How an amount of platform money is written.
 *
 * One Hub keeps money in one currency and names it in every answer that carries an amount
 * (`currency`, a three-letter code). An amount is therefore always read with its own currency,
 * and a page never prints a sign the response did not name. Mirrors `pa/lib/vocab/spend.py`
 * `money_words`, so a figure reads the same here as in a sentence the Hub wrote.
 */

import { moneyWords } from "../studio/words";

/** The currency a Hub answer names; null where the Hub serves no region and says nothing. */
export type Currency = string | null | undefined;

export interface Money {
  amount: number;
  currency: Currency;
}

const SIGNS: Record<string, string> = { CNY: "¥", USD: "$", EUR: "€", GBP: "£", JPY: "JP¥" };

/** The three-letter code, upper-cased; "" for anything that is not one. */
export function currencyCode(value: unknown): string {
  const code = typeof value === "string" ? value.trim().toUpperCase() : "";
  return /^[A-Z]{3}$/.test(code) ? code : "";
}

/** The word a unit label uses ("元", "美元"); the code itself when no word is known; "" without a currency. */
export function currencyUnit(currency: Currency): string {
  const code = currencyCode(currency);
  return code ? moneyWords(code) || code : "";
}

/** A label with its unit: `金额（美元）`; the bare label when the currency is unknown. */
export function withUnit(label: string, currency: Currency): string {
  const unit = currencyUnit(currency);
  return unit ? `${label}（${unit}）` : label;
}

function written(figure: string, currency: Currency): string {
  const code = currencyCode(currency);
  if (!code) return figure;
  const sign = SIGNS[code];
  return sign ? `${sign}${figure}` : `${moneyWords(code) || code} ${figure}`;
}

/** A balance or a charge, to the cent; a positive amount under a cent is shown as `<$0.01`, not as nothing. */
export function formatMoney({ amount, currency }: Money): string {
  if (amount > 0 && amount < 0.01) return `<${written("0.01", currency)}`;
  return written(amount.toLocaleString("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }), currency);
}

/** A unit price at the precision it was entered with; the Hub keeps six decimals. */
export function formatPrice({ amount, currency }: Money): string {
  return written(amount.toLocaleString("zh-CN", { maximumFractionDigits: 6 }), currency);
}
